import {
  CheckCheck, FileText, LifeBuoy, ListTree, Mic, Moon, PhoneForwarded, PhoneIncoming,
  PhoneMissed, PhoneOff, PhoneOutgoing, Voicemail,
} from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { formatPhone } from "../../../lib/phone";
import { callCounterpart, type CallRow } from "../../../lib/calls";
import type { CrmLabel } from "../../../lib/crmLabels";
import { LabelChips, fmtDuration, fmtStamp } from "../../ui";
import type { RowMenuProps } from "../../shell/ContextMenu";
import { useWorkCompact } from "../_shared/density";

/**
 * One call in the middle pane.
 *
 * Its own file for the same reason `ThreadRow` is: this is the one place where
 * the two densities are not the same markup with different numbers. Roomy, the
 * row stacks four lines - who, the number, the recording and transcript tags,
 * the labels. Compact, the tags move up onto the number's line, which takes a
 * whole line out of most rows; halving paddings never buys back that much and
 * only makes the list look cheap.
 *
 * The tile carries the OUTCOME first and the direction second, which is the
 * order an operator scans in: a missed call is a missed call whichever way it
 * went, and it is the one row in the list that needs doing something about. A
 * voicemail outranks even that - there is audio waiting - so it takes the tile
 * outright. Only when a call was answered does the arrow say which way it went.
 *
 * A refused caller is the one outcome drawn QUIETER than the others rather than
 * louder. See the `blocked` branch below.
 */
export function CallItem({
  call,
  open,
  labels,
  onOpen,
  menuProps,
}: {
  call: CallRow;
  open: boolean;
  labels: CrmLabel[];
  onOpen: (id: string) => void;
  /** Right-click and long-press, from the list's one shared menu. */
  menuProps: RowMenuProps;
}) {
  const t = useT();
  const compact = useWorkCompact();
  const answered = call.status === "completed";
  const missed = call.status === "no-answer" || call.status === "busy";
  // A caller the blocklist refused. Its own state rather than a kind of missed
  // call, and that is the whole reason the API gave it a status of its own: a
  // desk that turns away forty robots a week may not read as a desk that missed
  // forty calls. It still gets a row, because a list nobody can check is not a
  // way to find out the list has a customer on it.
  const blocked = call.status === "blocked";
  const inbound = call.direction === "inbound";

  // Drawn once and placed by whichever branch runs, so the two densities cannot
  // drift into showing a different set of tags.
  //
  // The ticket tag is last and is the one that answers "has this been dealt
  // with", which is the question the voicemail folder is worked for. It carries
  // the ticket NUMBER rather than a word: two rows both saying "ticket" tell an
  // operator nothing about whether they are the same one.
  //
  // "Outside hours" leads, because it changes what the row MEANS: a missed call
  // the office was shut for is not a call anybody dropped, and reading a night
  // of them as unanswered work is how somebody concludes the desk is
  // understaffed when it is the schedule that is wrong.
  const tags =
    call.afterHours ||
    call.intentDepartment ||
    call.divertedTo ||
    call.forwardedTo ||
    call.handledAt ||
    call.recordingStatus === "stored" ||
    call.transcriptStatus === "done" ||
    call.ticket ? (
      <span className="flex flex-none gap-1.5">
        {call.afterHours ? (
          <span className="crm-ctag" title={t("calls.pane.afterHoursHint")}>
            <Moon className="size-2.5" />
            {t("calls.pane.afterHours")}
          </span>
        ) : null}
        {/* What the CALLER said they wanted, pressed on their own keypad before
            anybody picked up. Beside "outside hours" because it is the other
            tag that changes what the row means rather than what was done to it -
            and because it is the one fact here that nobody at this desk
            supplied. */}
        {call.intentDepartment ? (
          <span className="crm-ctag" title={t("calls.pane.intentDepartmentHint")}>
            <ListTree className="size-2.5" />
            {call.intentDepartment}
          </span>
        ) : null}
        {/* Second, because it is what HAPPENED to the call rather than when it
            came in - and because it is the answer to the question the row
            otherwise raises: an answered call with no operator on it. */}
        {call.divertedTo ? (
          <span className="crm-ctag" title={t("calls.pane.divertedHint", { phone: formatPhone(call.divertedTo) })}>
            <PhoneForwarded className="size-2.5" />
            {t("calls.pane.diverted")}
          </span>
        ) : null}
        {/* Its sibling, and the row needs both: a diverted call was never
            offered to anybody here, a forwarded one rang the desk for twenty
            five seconds first. Only one of the two can be set on a call. */}
        {call.forwardedTo && !call.divertedTo ? (
          <span
            className="crm-ctag"
            title={t("calls.pane.forwardedHint", { phone: formatPhone(call.forwardedTo) })}
          >
            <PhoneForwarded className="size-2.5" />
            {t("calls.pane.forwarded")}
          </span>
        ) : null}
        {/* Ahead of the attachments, because it answers the question the row is
            being scanned for in the callback folder: is this one still mine to
            do. It carries no name - `handledBy` is empty on the ones an
            outgoing call closed by itself, and a tag that is sometimes a person
            and sometimes blank reads as a rendering fault. */}
        {call.handledAt ? (
          <span className="crm-ctag" title={t("calls.callback.doneHint")}>
            <CheckCheck className="size-2.5" />
            {t("calls.callback.doneTag")}
          </span>
        ) : null}
        {call.recordingStatus === "stored" ? (
          <span className="crm-ctag">
            <Mic className="size-2.5" />
            {t("calls.pane.recorded")}
          </span>
        ) : null}
        {call.transcriptStatus === "done" ? (
          <span className="crm-ctag">
            <FileText className="size-2.5" />
            {t("calls.detail.transcript")}
          </span>
        ) : null}
        {call.ticket ? (
          <span className="crm-ctag" title={t("calls.detail.ticket")}>
            <LifeBuoy className="size-2.5" />
            {call.ticket.number}
          </span>
        ) : null}
      </span>
    ) : null;

  // The other party, not a fixed column: on an inbound row `toNumber` is our own
  // Twilio line, and a list of our own number tells nobody anything.
  const other = callCounterpart(call);
  const shown = other ? formatPhone(other) : t("calls.incoming.withheld");
  // Who handled it, last on the line and only when there is an answer to give.
  //
  // An empty operator is NOT worth a word here. It is the normal state of an
  // inbound row until somebody claims it, and of one answered on a mobile - the
  // diverted and forwarded tags above already explain those, and a second
  // "nobody" on the same row would read as a fault rather than as a fact. The
  // one place the absence is worth spelling out is the callback queue, which is
  // what `calls.pane.operatorNone` is for.
  const who = call.operatorName ? t("calls.pane.operator", { name: call.operatorName }) : "";
  const meta = `${shown}${call.leadDomain ? ` · ${call.leadDomain}` : ""}${who ? ` · ${who}` : ""}`;

  // The number line, drawn once for both densities. `crm-mq` makes it read
  // itself out on hover instead of ending in an ellipsis, which is what this
  // line needs and the rest of the CRM's rows do not: compact puts the tags on
  // it, and the first thing a ticket number costs is the end of the phone
  // number - the half an operator was about to dial.
  //
  // The whole LINE slides, not the number inside it. Sliding the number alone
  // read as half a row moving: the digits arrived while the ticket number
  // beside them stayed cut off at the same place it had been. The outer span
  // clips, the inner one carries the number and - in compact, where they share
  // the line - the tags, and the two travel together.
  const number = (
    <span className={cn("crm-item-pre crm-mq is-line block", compact && "mt-[5px]")}>
      <span>
        <span className="min-w-0 flex-1 truncate">{meta}</span>
        {compact ? tags : null}
      </span>
    </span>
  );

  return (
    <button
      type="button"
      className={cn("crm-item items-center", open && "is-on")}
      onClick={() => onOpen(call.id)}
      {...menuProps}
    >
      <span
        className={cn(
          "crm-cdir",
          call.voicemail
            ? "is-vm"
            : blocked
              ? "is-blocked"
              : missed
                ? "is-miss"
                : inbound
                  ? "is-in"
                  : "is-out",
        )}
        title={t(
          call.voicemail
            ? "calls.pane.voicemail"
            : blocked
              ? "calls.pane.blockedCall"
              : inbound
                ? "calls.pane.inboundCall"
                : "calls.pane.outboundCall",
        )}
      >
        {call.voicemail ? (
          <Voicemail className="size-4" />
        ) : blocked ? (
          <PhoneOff className="size-4" />
        ) : missed ? (
          <PhoneMissed className="size-4" />
        ) : inbound ? (
          <PhoneIncoming className="size-4" />
        ) : (
          <PhoneOutgoing className="size-4" />
        )}
      </span>

      <span className="min-w-0 flex-1 text-left">
        {/* The name reads itself out on the same hover, so a company whose name
            is longer than the pane is not a row that can only ever be
            identified by opening it. */}
        <span className="crm-item-from crm-mq block">
          <span>{call.companyName ?? shown}</span>
        </span>

        {number}
        {/* Roomy keeps the tags on their own line, where they have the width to
            sit still; only compact folds them onto the number's line. */}
        {!compact && tags ? <span className="mt-[7px] flex">{tags}</span> : null}

        {/* Only a deep search puts anything here, and only on the rows it found
            in the written record. Same treatment the mail thread gives its
            preview line, because it is the same thing: the words that made this
            row an answer. */}
        {call.snippet ? <span className="crm-item-pre block">{call.snippet}</span> : null}

        <LabelChips labels={labels} tags={call.tags} className={compact ? "mt-1" : "mt-[7px]"} />
      </span>

      <span className="flex-none text-right">
        <span
          className={cn(
            "block font-bold tabular-nums",
            compact ? "text-[11.75px]" : "text-[12.25px]",
            !answered && "text-muted-foreground",
          )}
        >
          {answered ? fmtDuration(call.durationSec) : "-"}
        </span>
        <span className={cn("mt-[3px] block text-muted-foreground", compact ? "text-[10.5px]" : "text-[11px]")}>
          {fmtStamp(call.startedAt)}
        </span>
      </span>
    </button>
  );
}
